/**
 * Ground shift-library demographic / rule age factors when patient age and sex
 * are already known from structured data or narrative, so they are not offered as
 * nonsensical "next questions" (e.g. "Age > 65" when the chart already says 62).
 *
 * @module ddxDemographicFactorGrounding
 */

/** Mutually exclusive age-band factors from seedShiftFactors (integer age in years). */
export const AGE_BAND_SHIFT_FACTOR_IDS = [
  'factor_age_neonate',
  'factor_age_infant',
  'factor_age_toddler',
  'factor_age_child',
  'factor_age_adolescent',
  'factor_age_young_adult',
  'factor_age_middle',
  'factor_age_older_adult',
  'factor_age_elderly',
];

/**
 * Pick the single age-band factor id for a whole-year age, or null if uncertain.
 * Neonate (0–28 days) is not inferred from years alone.
 *
 * @param {number} ageYears
 * @returns {string | null}
 */
function ageBandFactorIdForYears(ageYears) {
  if (!Number.isFinite(ageYears) || ageYears < 0) return null;
  if (ageYears >= 80) return 'factor_age_elderly';
  if (ageYears >= 66) return 'factor_age_older_adult';
  if (ageYears >= 41) return 'factor_age_middle';
  if (ageYears >= 18) return 'factor_age_young_adult';
  if (ageYears >= 13) return 'factor_age_adolescent';
  if (ageYears >= 4) return 'factor_age_child';
  if (ageYears >= 1) return 'factor_age_toddler';
  return 'factor_age_infant';
}

/**
 * @param {number | null | undefined} ageYears
 * @param {string | null | undefined} sexNormalized - 'male' | 'female' or null
 * @returns {{
 *   resolvedTrueFactorIds: string[],
 *   excludeFromNextQuestionFactorIds: string[],
 * }}
 */
export function resolveDemographicShiftFactorsFromPatient(ageYears, sexNormalized) {
  /** @type {string[]} */
  const resolvedTrue = [];
  /** @type {string[]} */
  const exclude = [];

  const sex = String(sexNormalized || '').trim().toLowerCase();
  const isMale = sex === 'male';
  const isFemale = sex === 'female';

  if (typeof ageYears === 'number' && Number.isFinite(ageYears) && ageYears >= 0) {
    // Strict "Age > 65" (seedShiftLibrary) vs decision-rule "≥ 65"
    if (ageYears > 65) {
      resolvedTrue.push('factor_age_over_65');
    } else {
      exclude.push('factor_age_over_65');
    }

    if (ageYears >= 65) {
      resolvedTrue.push('factor_heart_age_65_or_above', 'factor_curb65_age_65_or_above');
    } else {
      exclude.push('factor_heart_age_65_or_above', 'factor_curb65_age_65_or_above');
    }

    // PERC: "Age < 50"
    if (ageYears < 50) {
      resolvedTrue.push('factor_perc_age_under_50');
    } else {
      exclude.push('factor_perc_age_under_50');
    }

    // Ottawa knee: age ≥ 55
    if (ageYears >= 55) {
      resolvedTrue.push('factor_ottawa_knee_age_55_or_above');
    } else {
      exclude.push('factor_ottawa_knee_age_55_or_above');
    }

    const band = ageBandFactorIdForYears(ageYears);
    if (band) {
      resolvedTrue.push(band);
      for (const id of AGE_BAND_SHIFT_FACTOR_IDS) {
        if (id !== band) exclude.push(id);
      }
    }
  }

  if (isMale) {
    resolvedTrue.push('factor_sex_male');
    exclude.push('factor_sex_female', 'factor_female_childbearing_age');
  } else if (isFemale) {
    resolvedTrue.push('factor_sex_female');
    exclude.push('factor_sex_male');
    if (typeof ageYears === 'number' && Number.isFinite(ageYears)) {
      if (ageYears >= 12 && ageYears <= 50) {
        resolvedTrue.push('factor_female_childbearing_age');
      } else {
        exclude.push('factor_female_childbearing_age');
      }
    }
  } else if (typeof ageYears === 'number' && Number.isFinite(ageYears)) {
    if (ageYears < 12 || ageYears > 50) {
      exclude.push('factor_female_childbearing_age');
    }
  }

  return {
    resolvedTrueFactorIds: [...new Set(resolvedTrue)],
    excludeFromNextQuestionFactorIds: [...new Set(exclude)],
  };
}
